import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';


export const usePlan = () => {
  const { currentUser } = useAuth();
  const [currentPlan, setCurrentPlan] = useState(null);
  // Los límites y flags del tier, tal como los define el backend. Vienen en la
  // misma respuesta que el plan y antes se descartaban.
  const [planFeatures, setPlanFeatures] = useState(null);
  const [moduloSalud, setModuloSalud] = useState(false);
  const [loading, setLoading] = useState(true);
  const [trialExpired, setTrialExpired] = useState(false);

  // `useCallback` con `currentUser` como única dependencia: la función sólo
  // depende de él. Sin esto se rearmaba en cada render, así que no se podía
  // poner en las dependencias del efecto sin provocar un pedido por render;
  // dejarla afuera —que era lo que había— hace que el efecto se quede con la
  // versión vieja de la función si algún día pasa a depender de algo más.
  const fetchCurrentPlan = useCallback(async () => {
    try {
      setLoading(true);
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/api/plan/current`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentPlan(data.plan);
        setPlanFeatures(data.features || null);
        /* Add-on, aparte de `features` a propósito: `features` es lo que declara
           el tier, y el módulo de salud no lo declara ningún tier — se vende
           suelto. Guardarlo adentro haría que un cambio de plan pareciera capaz
           de prenderlo o apagarlo. */
        setModuloSalud(data.moduloSalud === true);

        // Verificar si el trial ha expirado
        if (data.plan && data.plan.trialEndDate) {
          const trialEnd = new Date(data.plan.trialEndDate);
          const now = new Date();
          setTrialExpired(now > trialEnd && !data.plan.isPaid);
        }
      }
    } catch (error) {
      console.error('Error fetching current plan:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchCurrentPlan();
    } else {
      setLoading(false);
    }
  }, [currentUser, fetchCurrentPlan]);

  /**
   * ¿El plan actual habilita esta función?
   *
   * Antes esto adivinaba mirando el texto del id del plan
   * (`planId.includes('business')`), y no coincidía con lo que el backend
   * permite de verdad. El caso concreto: `TIERS` le da 10 empleados a
   * personal-pro y 15 a personal-pro-plus, pero ningún id personal contiene
   * "business", así que a un cliente que paga $29.999 se le escondía la gestión
   * de personal y de recursos que su plan sí incluye.
   *
   * Ahora se lee de `features`, que es el mismo objeto del tier que usa el
   * backend para autorizar (`TIERS[...].features` en server.js). Una sola
   * fuente de verdad: si mañana cambia un límite, la UI acompaña sola.
   */
  const hasFeature = (feature) => {
    if (!currentPlan) return false;

    // Un límite de 0 es "no incluido"; -1 es ilimitado.
    const permite = (limite) => limite === -1 || limite > 0;

    switch (feature) {
      case 'staff_management':
        return planFeatures ? permite(planFeatures.maxStaff) : false;

      case 'resources_management':
        return planFeatures ? permite(planFeatures.maxResources) : false;

      case 'multiple_locations':
        return Boolean(planFeatures?.multiLocation);

      case 'advanced_analytics':
        return Boolean(planFeatures?.analytics);

      case 'api_access':
        return Boolean(planFeatures?.integrations);

      // Estas cuatro también salen del tier ahora. Antes se adivinaban leyendo
      // el texto del id del plan, y eso producía dos resultados absurdos:
      //
      //   - `business-starter`, un plan pago, no contiene "pro" ni "plus", así
      //     que se quedaba sin bot de WhatsApp.
      //   - `enterprise` tampoco contiene ninguna de las dos, así que el plan
      //     más caro se quedaba sin bot Y sin soporte prioritario.
      //
      // Los flags viven en backend/src/planes.js y llegan en `features`, igual
      // que el resto. Los valores de hoy conservan el efecto que ya había para
      // los planes que se venden, y arreglan el de enterprise.
      //
      // OJO: cuando se implemente el complemento de WhatsApp del ítem [25]
      // —que se cobra por conversación y no por plan— esto deja de depender del
      // tier y pasa a mirar si el negocio contrató el complemento.
      case 'whatsapp_bot_manual':
        return Boolean(planFeatures?.whatsappBotManual);

      case 'whatsapp_bot_ai':
        return Boolean(planFeatures?.whatsappBotAI);

      case 'whatsapp_bot_any':
        return Boolean(planFeatures?.whatsappBotManual || planFeatures?.whatsappBotAI);

      case 'priority_support':
        return Boolean(planFeatures?.prioritySupport);

      default:
        return true; // Basic features available to all plans
    }
  };

  const canAccessBusinessPage = () => {
    if (!currentPlan) return false;
    
    // Si el trial ha expirado y no ha pagado, no puede acceder
    if (trialExpired) return false;
    
    // TODOS los planes pueden acceder al BusinessPage
    // Solo se requiere tener un plan activo (trial o pagado)
    const planId = currentPlan.id || currentPlan.planId;
    return planId && planId !== 'free'; // Cualquier plan excepto 'free'
  };

  const getDaysLeftInTrial = () => {
    if (!currentPlan || !currentPlan.trialEndDate || currentPlan.isPaid) return null;
    
    const trialEnd = new Date(currentPlan.trialEndDate);
    const now = new Date();
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  };

  const getPlanDisplayName = () => {
    if (!currentPlan) return 'Sin Plan';
    
    // Si hay metadata de display, usarla
    if (currentPlan.displayName) {
      return currentPlan.displayName;
    }
    
    // Si hay originalPlanId, usar el mapeo basado en eso
    const planId = currentPlan.originalPlanId || currentPlan.id || currentPlan.planId;
    const planNames = {
      'personal': 'Personal',
      'personal-pro': 'Personal Pro', 
      'personal-pro-plus': 'Personal Pro+',
      // Los mismos tres nombres que la landing y el backend. Antes acá decían
      // "Negocio", "Negocio Pro" y "Negocio Pro+", o sea que el panel llamaba al
      // plan distinto que la página donde el cliente lo compró.
      'business-starter': 'Starter',
      'business': 'Starter',
      'business-pro': 'Business',
      'business-pro-plus': 'Business Pro',
      'enterprise': 'Enterprise',
      'trial': 'Trial Gratuito'
    };
    
    return planNames[planId] || planId || 'Plan Desconocido';
  };

  const isTrialPlan = () => {
    return currentPlan && !currentPlan.isPaid && currentPlan.trialEndDate;
  };

  const refreshPlan = () => {
    if (currentUser) {
      fetchCurrentPlan();
    }
  };

  return {
    currentPlan,
    planFeatures,
    moduloSalud,
    loading,
    trialExpired,
    hasFeature,
    canAccessBusinessPage,
    getDaysLeftInTrial,
    getPlanDisplayName,
    isTrialPlan,
    refreshPlan
  };
};